import { ComponentProps, ReactNode } from 'react';

import { twMerge } from 'tailwind-merge';

import { Icon } from '../../Icon';
import { Text } from '../../Text';
import { Label } from '../Label';

import { Input } from './Input';

interface InputLabelProps {
  readonly size?: ComponentProps<typeof Input>['size'];
  readonly label?: ReactNode;
  readonly description?: ReactNode;
  readonly useDescriptionIcon?: boolean;
  readonly className?: string;
  readonly children?: ReactNode;
}

interface InputDescriptionProps {
  readonly size?: ComponentProps<typeof Input>['size'];
  readonly useIcon?: boolean;
  readonly className?: string;
  readonly children?: ReactNode;
}

interface LabelledInputProps extends ComponentProps<typeof Input>, Omit<InputLabelProps, 'className' | 'children'> {}

const sizes: Record<
  Exclude<ComponentProps<typeof Input>['size'], undefined>,
  {
    variant: ComponentProps<typeof Label>['variant'];
    className: string;
    descriptionVariant: ComponentProps<typeof Label>['variant'];
    descriptionClassName: string;
    iconSize: ComponentProps<typeof Icon>['size'];
    iconClassName: string;
  }
> = {
  xl: {
    variant: 'title-1',
    className: 'mb-3',
    descriptionVariant: 'body-1',
    descriptionClassName: 'mt-3',
    iconSize: 'lg',
    iconClassName: 'mr-2 -mt-[4px] -mb-[1px]',
  },
  l: {
    variant: 'subtitle-2',
    className: 'mb-2',
    descriptionVariant: 'body-1',
    descriptionClassName: 'mt-2',
    iconSize: 'lg',
    iconClassName: 'mr-2 -mt-[4px] -mb-[1px]',
  },
  m: {
    variant: 'body-2',
    className: 'mb-1.5',
    descriptionVariant: 'caption-2',
    descriptionClassName: 'mt-2',
    iconSize: 'sm',
    iconClassName: 'mr-1 -mt-[3px]',
  },
  s: {
    variant: 'body-1',
    className: 'mb-1.5',
    descriptionVariant: 'caption-1',
    descriptionClassName: 'mt-2',
    iconSize: 'sm',
    iconClassName: 'mr-1 -mt-[3px]',
  },
};

function InputDescription({ size = 'm', useIcon, children, className }: InputDescriptionProps) {
  return (
    <Text
      as="p"
      variant={sizes[size].descriptionVariant}
      className={twMerge('text-neutral-600', sizes[size].descriptionClassName, className)}
    >
      {useIcon ? (
        <Icon icon="maintenance" size={sizes[size].iconSize} className={twMerge(sizes[size].iconClassName, 'fill-current')} />
      ) : null}
      {children}
    </Text>
  );
}

function InputLabel({ label, size = 'm', description, useDescriptionIcon = true, className, children }: InputLabelProps) {
  return (
    <Label variant={sizes[size].variant} weight="normal" color="primary" className={twMerge(className)}>
      <Text as="span" className={twMerge(sizes[size].className, 'inline-block')}>
        {label}
      </Text>
      {children}
      {description ? (
        <InputDescription size={size} useIcon={useDescriptionIcon}>
          {description}
        </InputDescription>
      ) : null}
    </Label>
  );
}

function LabelledInput({ label, size = 'm', description, useDescriptionIcon, className, ...props }: LabelledInputProps) {
  return (
    <InputLabel label={label} size={size} description={description} useDescriptionIcon={useDescriptionIcon} className={className}>
      <Input size={size} {...props} />
    </InputLabel>
  );
}

export { LabelledInput, InputLabel, InputDescription };
